// ABOUTME: The discussion counters read straight from the server, past the cache: one item's (`limit: 0`), or those of
// ABOUTME: several items of one calendar at once — what a delete asks the person to confirm.
import type { ApolloClient } from "@apollo/client"
import {
  CalendarItemDiscussionsDocument,
  CalendarItemMessagesDocument,
  type DiscussionSummaryFragment,
} from "@/graphql/main/gql/graphql"

/**
 * The item's counters as the server has them now. Never folded by Apollo into an identical read sent before
 * them, which may answer with what has changed since (`frontend-discussion.md` §2).
 */
export async function fetchSummary(client: ApolloClient, itemId: string): Promise<DiscussionSummaryFragment> {
  const { data } = await client.query({
    query: CalendarItemMessagesDocument,
    variables: { calendarItemId: itemId, afterSeq: undefined, beforeSeq: undefined, limit: 0 },
    fetchPolicy: "no-cache",
    context: { queryDeduplication: false },
  })
  if (!data) throw new Error("calendarItemMessages answered without data")
  return data.calendarItemMessages.discussion
}

/**
 * The counters of several items of one person's calendar (`userID`; omitted, one's own), by item id. An item gone
 * meanwhile, or one whose count failed (`discussion: null`), is not in the map.
 */
export async function fetchSummaries(
  client: ApolloClient,
  userID: string | undefined,
  ids: readonly string[],
): Promise<Map<string, DiscussionSummaryFragment>> {
  if (ids.length === 0) return new Map()
  const { data } = await client.query({
    query: CalendarItemDiscussionsDocument,
    variables: { p: { userID, ids: [...ids] } },
    fetchPolicy: "no-cache",
    context: { queryDeduplication: false },
  })
  const summaries = new Map<string, DiscussionSummaryFragment>()
  for (const { id, discussion } of data?.calendarItems.records ?? []) {
    if (discussion) summaries.set(id, discussion)
  }
  return summaries
}
